/**
 * Machine effects that DISPATCH COMMANDS (Abhi's "output commands" model).
 *
 * The command-based alternative to `../effects/remixEffects.ts` (which calls
 * injected ports directly). Here the machine's `onEnter` effects announce what
 * happened by dispatching typed commands through an injected
 * `RemixCommandDispatcher`; any number of handlers react. The machine has zero
 * knowledge of consumers OR of the command transport (real bridge vs in-memory).
 *
 * State → command mapping:
 *   enter `thinking` → RemixStarted           (generation began)
 *   enter `ready`    → RemixReady + RemixGenerationEnded(reason: 'ready')
 *   enter `error`    → RemixErrored + RemixGenerationEnded(reason: 'error')
 *   enter `aborted`  → RemixAborted + RemixGenerationEnded(reason: 'aborted')
 *
 * `RemixGenerationEnded` means "the thinking/loading phase finished" (so a
 * handler can clear editor loading); its `reason` is derived from the
 * destination so a single handler can branch. It does NOT mean the flow is over
 * — a `ready` flow lives on until the user accepts/inserts or cancels. The
 * separate `RemixFlowEnded` command (dispatched by the store on teardown) marks
 * the true end of a flow's life.
 *
 * Additive; not wired into live flows.
 */

import type { RemixEffectHooks } from '../remixMachine';

import type { RemixCommandDispatcher } from './remixCommandBus';

/**
 * Build command-dispatching effect hooks around an injected dispatcher. Unlike
 * `buildRemixEffects`, this takes no ports — only the dispatcher. Product wiring
 * supplies a bridge-backed dispatcher; tests/local supply the in-memory one.
 */
export const buildRemixCommandEffects = (dispatcher: RemixCommandDispatcher): RemixEffectHooks => ({
	onEnterThinking: (ctx) =>
		dispatcher.dispatch({
			type: 'RemixStarted',
			remixId: ctx.remixId,
			conversationId: ctx.conversationId,
			targetNodeId: ctx.targetNodeId,
		}),

	onEnterReady: (ctx) => {
		dispatcher.dispatch({
			type: 'RemixReady',
			remixId: ctx.remixId,
			conversationId: ctx.conversationId,
			targetNodeId: ctx.targetNodeId,
			resultId: ctx.resultId,
		});
		dispatcher.dispatch({
			type: 'RemixGenerationEnded',
			remixId: ctx.remixId,
			conversationId: ctx.conversationId,
			targetNodeId: ctx.targetNodeId,
			reason: 'ready',
		});
	},

	onEnterError: (ctx) => {
		dispatcher.dispatch({
			type: 'RemixErrored',
			remixId: ctx.remixId,
			conversationId: ctx.conversationId,
			targetNodeId: ctx.targetNodeId,
		});
		dispatcher.dispatch({
			type: 'RemixGenerationEnded',
			remixId: ctx.remixId,
			conversationId: ctx.conversationId,
			targetNodeId: ctx.targetNodeId,
			reason: 'error',
		});
	},

	onEnterAborted: (ctx) => {
		dispatcher.dispatch({
			type: 'RemixAborted',
			remixId: ctx.remixId,
			conversationId: ctx.conversationId,
			targetNodeId: ctx.targetNodeId,
		});
		dispatcher.dispatch({
			type: 'RemixGenerationEnded',
			remixId: ctx.remixId,
			conversationId: ctx.conversationId,
			targetNodeId: ctx.targetNodeId,
			reason: 'aborted',
		});
	},
});
